import React, { useState, useCallback, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { getDownloadURL, getStorage, ref, uploadBytes } from "firebase/storage";
// External components
import { Button } from "@mui/material";
import { v4 as uuid } from "uuid";
// Components
import Loading from "../Loading";
// Utils
import { customToast as toast } from "../../utils";
// SCSS
import "react-image-crop/dist/ReactCrop.css";
import CustomCropper from "../CustomCropper/CustomCropper";

function CropperPlace({
  setFirebaseId,
  setFirebaseURL,
  onClose,
  upImg,
  isProduct,
  isCompany,
}) {
  const [saved, setSaved] = useState(false);
  const [crop, setCrop] = useState({
    unit: "%",
    width: 100,
    height: 100,
    aspect: 1,
  });
  const [completedCrop, setCompletedCrop] = useState(null);

  const imgRef = useRef(null);
  const previewCanvasRef = useRef(null);

  const handleSave = () => {
    setSaved(true);
    saveBlob(previewCanvasRef.current, completedCrop);
  };

  const saveBlob = (canvas, cropped) => {
    const storage = getStorage();
    if (!cropped || !canvas) {
      return;
    }
    canvas.toBlob(async blob => {
      const firebaseId = uuid();

      let storageRef;
      let finalFireBaseId;
      if (process.env.NODE_ENV === "production") {
        if (!isProduct) {
          finalFireBaseId = `places/dev/${firebaseId}`;
        } else if (isCompany) {
          finalFireBaseId = `companies/dev/${firebaseId}`;
        } else {
          finalFireBaseId = `market/dev/products/${firebaseId}`;
        }
        storageRef = ref(storage, finalFireBaseId);
      } else {
        if (!isProduct) {
          finalFireBaseId = `places/${firebaseId}`;
        } else if (isCompany) {
          finalFireBaseId = `companies/dev/${firebaseId}`;
        } else {
          finalFireBaseId = `market/products/${firebaseId}`;
        }
        storageRef = ref(storage, finalFireBaseId);
      }

      await uploadBytes(storageRef, blob).then(
        snapshot => {
          toast.success("¡Imagen guardada con éxito!");
        },
        error => {
          toast.error(error.message);
        },
      );
      const url = await getDownloadURL(storageRef);
      if (!url) {
        onClose();
        return toast.error("Link de imagen no válido");
      }
      setFirebaseURL(url);
      setFirebaseId(firebaseId);
      return onClose();
    });
  };

  const onLoad = useCallback(img => {
    imgRef.current = img;
  }, []);

  const handleCancel = () => {
    onClose();
  };

  useEffect(() => {
    if (!completedCrop || !previewCanvasRef.current || !imgRef.current) {
      return;
    }
    const image = imgRef.current;
    const canvas = previewCanvasRef.current;
    const crop1 = completedCrop;

    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    const ctx = canvas.getContext("2d");
    const pixelRatio = window.devicePixelRatio;

    canvas.width = crop1.width * pixelRatio * scaleX;
    canvas.height = crop1.height * pixelRatio * scaleY;

    ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
    ctx.imageSmoothingQuality = "high";

    ctx.drawImage(
      image,
      crop1.x * scaleX,
      crop1.y * scaleY,
      crop1.width * scaleX,
      crop1.height * scaleY,
      0,
      0,
      crop1.width * scaleX,
      crop1.height * scaleY,
    );
  }, [completedCrop]);

  return (
    <div
      style={{
        width: "100%",
        height: "25rem",
        display: "flex",
        justifyContent: "space-between",
        flexDirection: "column",
        gap: "2rem",
      }}
    >
      <CustomCropper
        crop={crop}
        imgRef={imgRef}
        previewCanvasRef={previewCanvasRef}
        style={{
          width: Math.round(completedCrop?.width ?? 0),
          height: Math.round(completedCrop?.height ?? 0),
        }}
        upImg={upImg}
        onChange={c => setCrop(c)}
        onComplete={c => setCompletedCrop(c)}
        onLoad={onLoad}
      />
      {saved && <Loading />}
      {!saved && (
        <div className="content-flex-row-between">
          <Button
            color="primary"
            disabled={!completedCrop?.width || !completedCrop?.height}
            type="button"
            variant="contained"
            onClick={handleSave}
          >
            Recortar y Guardar
          </Button>
          <Button
            className="CropperPhoto-button"
            color="primary"
            variant="outlined"
            onClick={handleCancel}
          >
            Cancelar
          </Button>
        </div>
      )}
    </div>
  );
}
CropperPlace.propTypes = {
  onClose: PropTypes.func,
  upImg: PropTypes.string.isRequired,
  setFirebaseId: PropTypes.func.isRequired,
  setFirebaseURL: PropTypes.func.isRequired,
  isProduct: PropTypes.bool,
  isCompany: PropTypes.bool,
};
CropperPlace.defaultProps = {
  onClose: () => null,
  isProduct: false,
  isCompany: false,
};
export default CropperPlace;
